import { AxiosError } from "axios";

import { buildPaginationQuery } from "@/helpers/request-helpers";
import { apiT2B } from "@/lib/axios";

export interface CustomerSalesProduct {
  id: number;
  salesProduct: {
    id: number;
    code: string;
    name: string;
    description: string;
  };
  customerId: number;
  active: boolean;
  rowDate: string;
}

export interface CustomerSalesProductResponse {
  totalCount: number;
  pageNumber: number;
  pageSize: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  items: CustomerSalesProduct[];
}

class CustomerSalesProductService {
  private baseUrl = "api/v1/customersalesproduct";

  async getAll(pageNumber = 1, pageSize = 150): Promise<CustomerSalesProductResponse> {
    try {
      const paginationQuery = buildPaginationQuery({ pageNumber, pageSize });
      const response = await apiT2B.get(`${this.baseUrl}?${paginationQuery}`);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error("Invalid token specified");
        }
        throw new Error(error.response?.data?.message || "Failed to fetch customer sales products");
      }
      throw new Error("Something went wrong");
    }
  }

  async getById(id: number): Promise<CustomerSalesProduct> {
    try {
      const response = await apiT2B.get(`${this.baseUrl}/${id}`);
      return response.data;
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error("Invalid token specified");
        }
        throw new Error(error.response?.data?.message || "Failed to fetch customer sales product");
      }
      throw new Error("Something went wrong");
    }
  }

  async update(id: number, data: Partial<CustomerSalesProduct>): Promise<void> {
    try {
      await apiT2B.put(`${this.baseUrl}/${id}`, data);
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error("Invalid token specified");
        }
        throw new Error(error.response?.data?.message || "Failed to update customer sales product");
      }
      throw new Error("Something went wrong");
    }
  }

  async getCustomerSalesProductById(id: number): Promise<CustomerSalesProduct> {
    return this.getById(id);
  }

  async deleteCustomerSalesProduct(id: number): Promise<void> {
    try {
      await apiT2B.delete(`${this.baseUrl}/${id}`);
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          throw new Error("Invalid token specified");
        }
        throw new Error(error.response?.data?.message || "Failed to delete customer sales product");
      }
      throw new Error("Something went wrong");
    }
  }
}

export const customerSalesProductService = new CustomerSalesProductService();
